import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAIWorkspace } from "@/hooks/useAIWorkspace";
import { RecruiterSkeleton } from "@/components/shared";
import { HomeComposer } from "@/components/home/HomeComposer";
import { configSummary } from "@/components/home/ComposerSettings";
import { KpiTile, NeedsYou, ActiveRoles, buildNeeds } from "@/components/home/HomePanels";
import { FirstRun } from "@/components/home/FirstRun";
import { RecentChats } from "@/components/home/RecentChats";
import { PausedCandidates } from "@/components/proctor/PausedCandidates";
import { Building2, ArrowRight } from "lucide-react";

const STARTERS = [
  { text: "Which candidates have waited more than 5 days?", mode: "Pipeline" },
  { text: "Draft a JD for an AI engineer", mode: "Create Job" },
  { text: "Summarise this week's funnel", mode: "Analytics" },
];

export default function RecruiterHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const openChat = useAIWorkspace();
  const [d, setD] = useState(null);

  useEffect(() => {
    const safe = (p, fb) => api.get(p).then((r) => r.data).catch(() => fb);
    Promise.all([safe("/dashboard/recruiter", {}), safe("/jobs", []), safe("/dashboard/brief", null), safe("/company/profile", null), safe("/interviews", []), safe("/org", null), safe("/ai/sessions", []), safe("/me/signoff-requests", [])])
      .then(([stats, jobs, brief, company, interviews, org, sessions, signoffs]) => setD({ stats, jobs, brief, company, interviews, org, sessions, signoffs }));
  }, []);

  const run = (prompt, { mode, depth, config }) => {
    const extras = config ? configSummary(config) : "";
    const body = [prompt, depth && depth !== "Standard" ? `Depth: ${depth}` : "", extras ? `Hiring configuration: ${extras}` : ""].filter(Boolean).join("\n");
    openChat(body, { mode, context: { config, depth }, previewUrl: "/recruiter/jobs" });
  };

  const needs = useMemo(() => (d ? buildNeeds(d.jobs, d.interviews, d.signoffs) : []), [d]);
  const first = user?.name?.split(" ")[0] || "there";
  if (!d) return <div className="recruiter-page"><RecruiterSkeleton variant="analytics" label="Preparing your home…" /></div>;

  const real = d.jobs.filter((j) => !j.is_sample);
  if (!real.length) return <FirstRun name={first} onSubmit={run} company={d.company} org={d.org} sample={d.jobs.find((j) => j.is_sample)} isOwner={user?.org_role === "owner"} />;

  const live = d.jobs.filter((j) => j.status === "published").length;
  const drafts = d.jobs.filter((j) => j.status === "draft").length;
  const todayIv = d.interviews.filter((i) => i.status === "scheduled" && new Date(i.when).toDateString() === new Date().toDateString()).length;
  const s = d.stats || {};
  const date = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="pb-24" data-testid="recruiter-home">
      <section className="hs-hero pt-12 sm:pt-[60px] short:pt-8 pb-12 short:pb-8" data-testid="home-prompt-launcher">
        <div className="max-w-[860px] mx-auto px-4 space-y-5">
          <div className="text-center space-y-2 hs-rise">
            <div className="text-sm text-muted-foreground">{date}</div>
            <h1 className="text-3xl sm:text-[42px] font-display font-semibold tracking-tight leading-[1.1]">What are we hiring for today, {first}?</h1>
          </div>
          <div className="hs-rise" style={{ animationDelay: "70ms" }}><HomeComposer onSubmit={run} userId={user?.id} /></div>
          <div className="flex flex-wrap justify-center gap-2 hs-rise" style={{ animationDelay: "140ms" }} data-testid="home-prompt-shortcuts">
            {STARTERS.map((st, i) => <button key={st.text} className="hs-chip" onClick={() => run(st.text, { mode: st.mode })} data-testid={`home-prompt-shortcut-${i}`}>{st.text}</button>)}
          </div>
          <div className="hs-rise" style={{ animationDelay: "210ms" }}><RecentChats sessions={d.sessions} jobs={d.jobs} /></div>
        </div>
      </section>

      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 space-y-6">
        <PausedCandidates />
        {d.company && !d.company.complete && (
          <button onClick={() => nav("/account")} className="w-full hs-card flex items-center gap-3 px-5 py-3.5 text-left border-amber-400/30 hover:border-amber-400/50" data-testid="company-profile-nudge">
            <Building2 className="h-4 w-4 text-amber-500" /><span className="flex-1 text-sm">Complete your company profile — {d.company.completion}% done. It brands your careers page and emails.</span><ArrowRight className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 hs-stagger" data-testid="dashboard-metrics">
          <KpiTile value={live} label="Active roles" sub={`${drafts} draft${drafts === 1 ? "" : "s"}`} onClick={() => nav("/recruiter/jobs")} testId="metric-active-jobs" />
          <KpiTile value={s.total_candidates ?? 0} label="Candidates" sub={`across ${live} live role${live === 1 ? "" : "s"}`} onClick={() => nav("/recruiter/pipeline")} testId="metric-candidates" />
          <KpiTile value={s.in_interview ?? 0} label="In interview" sub={todayIv ? `${todayIv} today` : "none today"} onClick={() => nav("/recruiter/interviews")} testId="metric-in-interview" />
          <KpiTile value={s.needs_review ?? 0} label="Need review" sub={s.needs_review ? "awaiting a decision" : "all caught up"} highlight={s.needs_review > 0} onClick={() => nav("/recruiter/pipeline")} testId="metric-needs-review" />
        </div>
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 items-start">
          <NeedsYou items={needs} brief={d.brief} />
          <ActiveRoles jobs={d.jobs} />
        </div>
      </div>
    </div>
  );
}
